import { AlertTriangle, ArrowRight, ClipboardCheck } from 'lucide-react';
import { useState } from 'react';
import type {
  SkuActivationCheck,
  SkuActivationCheckStatus,
  SkuActivationMutationEvidence,
  SkuActivationPlanSnapshot,
  SkuActivationReadinessRequest,
  SkuActivationReadinessResponse,
  SkuActivationStageId,
} from '@nadoutong/contracts';
import { useNavigate } from 'react-router-dom';
import { OpsApiError } from '../api/client.ts';
import {
  useCompleteSkuActivationMutation,
  useCreateSkuActivationPlan,
  useLoadSkuActivationPlan,
  useSkuActivationReadiness,
} from '../api/skuActivations.ts';
import { CustomerRef } from '../components/identity/CustomerRef.tsx';
import { coerceErrorMessage, useToast } from '../components/Toast.tsx';
import { useT } from '../i18n/index.ts';
import {
  Button,
  Chip,
  EmptyState,
  FormField,
  Input,
  PageHeader,
  Panel,
  Select,
  Textarea,
  type ChipTone,
} from '../ui/index.ts';

type Environment = 'dev' | 'staging' | 'production';

const ENVIRONMENT_OPTIONS = [
  { value: 'dev', label: 'dev' },
  { value: 'staging', label: 'staging' },
  { value: 'production', label: 'production' },
] as const;

export function inferSkuActivationEnvironment(hostname?: string): Environment {
  const host = hostname ?? (typeof window === 'undefined' ? '' : window.location.hostname);
  if (host === 'ops.nadoutong.org') return 'production';
  if (host.includes('staging')) return 'staging';
  return 'dev';
}

export function parseSkuActivationIds(raw: string): string[] {
  return [...new Set(raw.split(/[\s,]+/u).map((value) => value.trim()).filter(Boolean))].sort();
}

function stageLabel(id: SkuActivationStageId, t: ReturnType<typeof useT>): string {
  return t(`sku_activation.stage.${id}`);
}

function statusTone(status: SkuActivationCheckStatus): ChipTone {
  if (status === 'ready') return 'ok';
  if (status === 'warning' || status === 'waiting') return 'warn';
  if (status === 'blocked' || status === 'degraded') return 'bad';
  return 'neutral';
}

function statusLabel(status: SkuActivationCheckStatus, t: ReturnType<typeof useT>): string {
  return t(`sku_activation.status.${status}`);
}

function mutationKindLabel(kind: SkuActivationPlanSnapshot['mutations'][number]['kind'], t: ReturnType<typeof useT>): string {
  return t(`sku_activation.plan.kind.${kind}`);
}

function mutationStatusLabel(status: 'pending' | 'completed', t: ReturnType<typeof useT>): string {
  return status === 'completed' ? t('sku_activation.plan.mutation_status.completed') : t('sku_activation.plan.mutation_status.pending');
}

function planStatusLabel(status: SkuActivationPlanSnapshot['status'], t: ReturnType<typeof useT>): string {
  if (status === 'completed') return t('sku_activation.plan.status.completed');
  if (status === 'cancelled') return t('sku_activation.plan.status.cancelled');
  return t('sku_activation.plan.status.active');
}

function requirementReasonLabel(reason: string | undefined, t: ReturnType<typeof useT>): string {
  if (reason === undefined) return t('sku_activation.plan.required_default');
  const known: Record<string, Parameters<typeof t>[0]> = {
    'platform model is already listed': 'sku_activation.plan.reason.platform_listed',
    'platform model is not listed': 'sku_activation.plan.reason.platform_not_listed',
    'key alias already maps to the SKU': 'sku_activation.plan.reason.key_alias_ready',
    'key alias is missing or targets another SKU': 'sku_activation.plan.reason.key_alias_missing',
    'no routable exact capability chain exists': 'sku_activation.plan.reason.supply_missing',
    'routable exact capability chain already exists': 'sku_activation.plan.reason.supply_ready',
    'no routing target has a routable exact capability chain': 'sku_activation.plan.reason.routing_missing',
    'routing target has a routable exact capability chain': 'sku_activation.plan.reason.routing_ready',
  };
  const key = known[reason];
  return key === undefined ? t('sku_activation.plan.reason.server', { reason }) : t(key);
}

function mirrorStatusLabel(status: string, t: ReturnType<typeof useT>): string {
  if (status === 'delivered') return t('sku_activation.plan.mirror_status.delivered');
  if (status === 'failed') return t('sku_activation.plan.mirror_status.failed');
  if (status === 'not_applicable') return t('sku_activation.plan.mirror_status.not_applicable');
  if (status === 'unknown') return t('sku_activation.plan.mirror_status.unknown');
  return t('sku_activation.plan.mirror_status.pending');
}

function initialSelectedCheck(response: SkuActivationReadinessResponse): SkuActivationCheck | null {
  if (response.first_blocker !== null) {
    for (const stage of response.stages) {
      const check = stage.checks.find((row) => row.id === response.first_blocker?.check_id);
      if (check !== undefined) return check;
    }
  }
  return response.stages[0]?.checks[0] ?? null;
}

export function SkuActivationReadinessPage(): JSX.Element {
  const t = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const mutation = useSkuActivationReadiness();
  const createPlan = useCreateSkuActivationPlan();
  const loadPlan = useLoadSkuActivationPlan();
  const completeMutation = useCompleteSkuActivationMutation();
  const [environment, setEnvironment] = useState<Environment>(inferSkuActivationEnvironment());
  const [skuIds, setSkuIds] = useState('');
  const [orgId, setOrgId] = useState('');
  const [apiKeyId, setApiKeyId] = useState('');
  const [configIds, setConfigIds] = useState('');
  const [upstreamIds, setUpstreamIds] = useState('');
  const [planIdInput, setPlanIdInput] = useState('');
  const [plan, setPlan] = useState<SkuActivationPlanSnapshot | null>(null);
  const [completionJson, setCompletionJson] = useState<Record<string, string>>({});
  const [mirrorStatus, setMirrorStatus] = useState<Record<string, string>>({});
  const [completingId, setCompletingId] = useState<string | null>(null);
  const [completionErrors, setCompletionErrors] = useState<Record<string, string>>({});
  const [response, setResponse] = useState<SkuActivationReadinessResponse | null>(null);
  const [selectedCheck, setSelectedCheck] = useState<SkuActivationCheck | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  function invalidateResult(): void {
    setResponse(null);
    setSelectedCheck(null);
  }

  function adoptPlan(next: SkuActivationPlanSnapshot): void {
    setPlan(next); setPlanIdInput(next.id); setEnvironment(next.environment as Environment);
    setSkuIds(next.sku_ids.join('\n')); setOrgId(next.org_id ?? ''); setApiKeyId(next.api_key_id ?? '');
    setConfigIds(next.config_ids.join('\n')); setUpstreamIds(next.allowed_upstream_ids.join('\n'));
    setCompletionErrors({}); invalidateResult();
  }

  async function handleRun(): Promise<void> {
    if (plan === null) { setFormError(t('sku_activation.plan.required_first')); return; }
    setFormError(null);
    const request: SkuActivationReadinessRequest = {
      activation_plan_id: plan.id,
      environment: plan.environment,
      sku_ids: plan.sku_ids,
      ...(plan.org_id === null ? {} : { org_id: plan.org_id }),
      ...(plan.api_key_id === null ? {} : { api_key_id: plan.api_key_id }),
      config_ids: plan.config_ids,
      allowed_upstream_ids: plan.allowed_upstream_ids,
    };
    try {
      const next = await mutation.mutateAsync(request);
      setResponse(next);
      setSelectedCheck(initialSelectedCheck(next));
      toast.success(t('sku_activation.toast.completed'));
    } catch (error) {
      const message = error instanceof OpsApiError
        ? `${error.code}: ${error.message}`
        : coerceErrorMessage(error);
      toast.error(message);
    }
  }

  async function handleCreatePlan(): Promise<void> {
    const skus = parseSkuActivationIds(skuIds);
    const upstreams = parseSkuActivationIds(upstreamIds);
    const configs = parseSkuActivationIds(configIds);
    const normalizedOrg = orgId.trim();
    const normalizedKey = apiKeyId.trim();
    if (skus.length === 0 || upstreams.length === 0) {
      setFormError(t('sku_activation.form.scope_required')); return;
    }
    if (normalizedKey.length > 0 && normalizedOrg.length === 0) {
      setFormError(t('sku_activation.form.org_required_for_key')); return;
    }
    try {
      const created = await createPlan.mutateAsync({
        environment, sku_ids: skus, config_ids: configs, allowed_upstream_ids: upstreams,
        ...(normalizedOrg.length === 0 ? {} : { org_id: normalizedOrg }),
        ...(normalizedKey.length === 0 ? {} : { api_key_id: normalizedKey }),
      });
      adoptPlan(created.plan); setFormError(null);
    } catch (error) { toast.error(coerceErrorMessage(error)); }
  }

  async function handleLoadPlan(): Promise<void> {
    if (planIdInput.trim().length === 0) return;
    try { const loaded = await loadPlan.mutateAsync(planIdInput.trim()); adoptPlan(loaded.plan); }
    catch (error) { toast.error(coerceErrorMessage(error)); }
  }

  async function handleComplete(planMutationId: string): Promise<void> {
    if (plan === null) return;
    setCompletingId(planMutationId);
    setCompletionErrors((current) => ({ ...current, [planMutationId]: '' }));
    try {
      const parsed = JSON.parse(completionJson[planMutationId] ?? '') as SkuActivationMutationEvidence;
      const completed = await completeMutation.mutateAsync({ planId: plan.id, mutationId: planMutationId, evidence: parsed });
      setPlan(completed.plan);
      setMirrorStatus((current) => ({ ...current, [planMutationId]: completed.mirror?.status ?? 'unknown' }));
      setCompletionJson((current) => ({ ...current, [planMutationId]: '' })); invalidateResult();
    } catch (error) {
      const message = coerceErrorMessage(error);
      setCompletionErrors((current) => ({ ...current, [planMutationId]: message }));
      toast.error(message);
    } finally { setCompletingId(null); }
  }

  return (
    <section className="flex flex-col gap-6" data-testid="sku-activation-readiness-page">
      <PageHeader
        title={t('sku_activation.title')}
        sub={t('sku_activation.subtitle')}
        actions={<Chip tone="neutral">{t('sku_activation.read_only')}</Chip>}
      />

      {(plan?.environment ?? environment) === 'production' ? (
        <div
          className="flex items-start gap-3 rounded-lg border border-bad-line bg-bad-dim px-4 py-3 text-body text-bad-bright"
          role="alert"
          data-testid="sku-activation-production-warning"
        >
          <AlertTriangle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
          <div className="flex flex-col gap-1">
            <strong>{t('sku_activation.production.title')}</strong>
            <span className="text-dense">{t('sku_activation.production.body')}</span>
          </div>
        </div>
      ) : null}

      <Panel caption={t('sku_activation.scope.title')} padded testId="sku-activation-scope">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <FormField label={t('sku_activation.form.environment')} fieldId="activation-environment" required>
            <Select
              id="activation-environment"
              value={environment}
              onValueChange={(value) => { invalidateResult(); setEnvironment(value as Environment); }}
              options={ENVIRONMENT_OPTIONS}
              disabled={plan !== null}
              mono
              testId="sku-activation-environment"
            />
          </FormField>
          <FormField
            label={t('sku_activation.form.org_id')}
            fieldId="activation-org-id"
            hint={t('sku_activation.form.optional')}
          >
            <Input id="activation-org-id" value={orgId} disabled={plan !== null} onChange={(event) => { invalidateResult(); setOrgId(event.target.value); }} mono />
          </FormField>
          <FormField
            label={t('sku_activation.form.api_key_id')}
            fieldId="activation-key-id"
            hint={t('sku_activation.form.key_hint')}
          >
            <Input id="activation-key-id" value={apiKeyId} disabled={plan !== null} onChange={(event) => { invalidateResult(); setApiKeyId(event.target.value); }} mono />
          </FormField>
          <FormField
            label={t('sku_activation.form.sku_ids')}
            fieldId="activation-sku-ids"
            required
            hint={t('sku_activation.form.list_hint')}
            error={formError}
            className="lg:col-span-3"
          >
            <Textarea
              id="activation-sku-ids"
              value={skuIds}
              onChange={(event) => { invalidateResult(); setSkuIds(event.target.value); }}
              mono
              rows={3}
              invalid={formError !== null}
              disabled={plan !== null}
              aria-describedby={formError === null ? undefined : 'activation-sku-ids-error'}
            />
          </FormField>
          <FormField
            label={t('sku_activation.form.upstream_ids')}
            fieldId="activation-upstream-ids"
            hint={t('sku_activation.form.upstream_hint')}
            className="lg:col-span-2"
          >
            <Textarea
              id="activation-upstream-ids"
              value={upstreamIds}
              onChange={(event) => { invalidateResult(); setUpstreamIds(event.target.value); }}
              mono
              rows={2}
              disabled={plan !== null}
            />
          </FormField>
          <FormField
            label={t('sku_activation.form.config_ids')}
            fieldId="activation-config-ids"
            hint={t('sku_activation.form.config_hint')}
          >
            <Textarea
              id="activation-config-ids"
              value={configIds}
              onChange={(event) => { invalidateResult(); setConfigIds(event.target.value); }}
              mono
              rows={2}
              disabled={plan !== null}
            />
          </FormField>
        </div>
        <div className="mt-4 flex flex-wrap items-end gap-3 border-t border-line-soft pt-4">
          <div className="min-w-64 flex-1">
            <FormField label={t('sku_activation.plan.id')} fieldId="activation-plan-id" hint={t('sku_activation.plan.load_hint')}>
              <Input id="activation-plan-id" value={planIdInput} onChange={(event) => setPlanIdInput(event.target.value)} mono />
            </FormField>
          </div>
          <Button variant="secondary" loading={loadPlan.isPending} onClick={() => void handleLoadPlan()}
            data-testid="sku-activation-load-plan">{t('sku_activation.plan.load')}</Button>
          <Button variant="secondary" loading={createPlan.isPending} onClick={() => void handleCreatePlan()}
            disabled={plan !== null} data-testid="sku-activation-create-plan">{t('sku_activation.plan.create')}</Button>
          {plan === null ? null : <Button variant="ghost" onClick={() => { setPlan(null); setPlanIdInput(''); invalidateResult(); }}
            data-testid="sku-activation-clear-plan">{t('sku_activation.plan.clear')}</Button>}
          <Button
            variant="primary"
            loading={mutation.isPending}
            disabled={plan === null}
            onClick={() => void handleRun()}
            data-testid="sku-activation-run"
          >
            {mutation.isPending ? t('sku_activation.action.running') : t('sku_activation.action.run')}
          </Button>
        </div>
      </Panel>

      {plan === null ? null : (
        <Panel caption={t('sku_activation.plan.title')} count={plan.mutations.length} padded testId="sku-activation-plan">
          <div className="mb-4 grid grid-cols-1 gap-2 font-mono text-micro text-paper-mid md:grid-cols-3">
            <span data-testid="sku-activation-plan-id">{t('sku_activation.plan.id')}: {plan.id}</span>
            <span>{t('sku_activation.plan.environment')}: {plan.environment} · {t('sku_activation.plan.status_label')}: {planStatusLabel(plan.status, t)}</span>
            <span>{t('sku_activation.plan.skus')}: {plan.sku_ids.join(', ')}</span>
            <span>{t('sku_activation.plan.org')}: {plan.org_id === null ? t('sku_activation.plan.none') : <CustomerRef orgId={plan.org_id} />}</span>
            <span>{t('sku_activation.plan.key')}: {plan.api_key_id ?? t('sku_activation.plan.none')}</span>
            <span>{t('sku_activation.plan.upstreams')}: {plan.allowed_upstream_ids.join(', ')}</span>
            <span>{t('sku_activation.plan.configs')}: {plan.config_ids.length === 0 ? t('sku_activation.plan.none') : plan.config_ids.join(', ')}</span>
          </div>
          <ol className="m-0 list-none divide-y divide-line-soft p-0" aria-label={t('sku_activation.plan.checklist')} data-testid="sku-activation-plan-mutations">
            {plan.mutations.map((item) => (
              <li key={item.id} className="grid grid-cols-1 gap-3 py-3 lg:grid-cols-[1fr_2fr_auto]">
                <div>
                  <div className="flex items-center gap-2"><h3 className="m-0 font-mono text-body">{mutationKindLabel(item.kind, t)}</h3>
                    <Chip tone={item.status === 'completed' ? 'ok' : 'warn'}>{mutationStatusLabel(item.status, t)}</Chip></div>
                  <p className="m-0 font-mono text-micro text-paper-low">{item.sku_id} · {item.owner_id}</p>
                  <p className="m-0 font-mono text-micro text-paper-low">
                    {item.requirement?.status === 'not_required'
                      ? t('sku_activation.plan.requirement_status.not_required')
                      : t('sku_activation.plan.requirement_status.required')} · {requirementReasonLabel(item.requirement?.reason, t)}
                  </p>
                  {item.evidence === null ? null : (
                    <p className="m-0 font-mono text-micro text-paper-low">
                      {t('sku_activation.plan.receipt')}: {item.evidence.receipt_id ?? t('sku_activation.plan.none')} · {t('sku_activation.plan.revision')}: {item.evidence.owner_revision ?? t('sku_activation.plan.none')}
                    </p>
                  )}
                  <p className="m-0 font-mono text-micro text-paper-low">
                    {t('sku_activation.plan.mirror')}: {mirrorStatusLabel(mirrorStatus[item.id] ??
                      (item.kind === 'platform_shelf' || item.kind === 'key_alias' ? 'not_applicable' : 'pending'), t)}
                  </p>
                </div>
                {item.status === 'completed' ? <span className="text-dense text-ok">
                  {item.requirement?.status === 'not_required' ? t('sku_activation.plan.not_required') : t('sku_activation.plan.completed')}
                </span> : (
                  <Textarea id={`activation-completion-${item.id}`} value={completionJson[item.id] ?? ''} rows={3} mono
                    aria-label={t('sku_activation.plan.completion_aria', { mutation: item.id })}
                    aria-describedby={completionErrors[item.id] ? `activation-completion-${item.id}-error` : undefined}
                    placeholder={t('sku_activation.plan.completion_placeholder')}
                    onChange={(event) => setCompletionJson((current) => ({ ...current, [item.id]: event.target.value }))} />
                )}
                {item.status === 'completed' ? null : (
                  <Button variant="secondary" loading={completingId === item.id} disabled={completingId !== null && completingId !== item.id}
                    onClick={() => void handleComplete(item.id)} data-testid={`sku-activation-complete-${item.id}`}>
                    {t('sku_activation.plan.complete')}
                  </Button>
                )}
                {completionErrors[item.id] ? <p id={`activation-completion-${item.id}-error`} role="alert" className="m-0 text-dense text-bad lg:col-span-3">{completionErrors[item.id]}</p> : null}
              </li>
            ))}
          </ol>
        </Panel>
      )}

      {response === null ? (
        <EmptyState
          title={t('sku_activation.empty.title')}
          hint={t('sku_activation.empty.body')}
          icon={<ClipboardCheck aria-hidden="true" className="h-5 w-5" />}
          testId="sku-activation-empty"
        />
      ) : (
        <div className="flex flex-col gap-4" aria-live="polite">
          <ResultSummary response={response} onNavigate={navigate} />
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
            <div className="flex flex-col gap-4 xl:col-span-2">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {response.stages.map((item, index) => (
                  <Panel
                    key={item.id}
                    caption={`${String(index + 1)}. ${stageLabel(item.id, t)}`}
                    count={item.checks.length}
                    testId={`sku-activation-stage-${item.id}`}
                    actions={<Chip tone={statusTone(item.status)}>{statusLabel(item.status, t)}</Chip>}
                  >
                    <div className="divide-y divide-line-soft">
                      {item.checks.map((check) => (
                        <div key={check.id} className="flex items-start gap-3 px-4 py-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-mono text-label text-paper">{t('sku_activation.server_code')}: {check.code}</span>
                              <Chip tone={statusTone(check.status)} lamp={false}>
                                {statusLabel(check.status, t)}
                              </Chip>
                            </div>
                            <p className="mb-0 mt-1 text-dense text-paper-low">{check.message}</p>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setSelectedCheck(check)}
                            data-testid={`sku-activation-inspect-${check.id}`}
                          >
                            {t('sku_activation.action.inspect')}
                          </Button>
                        </div>
                      ))}
                    </div>
                  </Panel>
                ))}
              </div>
              <LineagePanel response={response} />
            </div>
            <EvidencePanel check={selectedCheck} onNavigate={navigate} />
          </div>
        </div>
      )}
    </section>
  );
}

function ResultSummary({
  response,
  onNavigate,
}: {
  readonly response: SkuActivationReadinessResponse;
  readonly onNavigate: (path: string) => void;
}): JSX.Element {
  const t = useT();
  const blocker = response.first_blocker;
  return (
    <Panel
      caption={t('sku_activation.result.title')}
      padded
      actions={<Chip tone={response.ready ? 'ok' : 'bad'}>{response.ready ? t('sku_activation.result.ready') : t('sku_activation.result.blocked')}</Chip>}
      testId="sku-activation-result-summary"
    >
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="flex flex-col gap-1">
          <span className="text-label text-paper-low">{t('sku_activation.result.fingerprint')}</span>
          <code className="break-all text-micro text-paper-mid">{response.fingerprint}</code>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-label text-paper-low">{t('sku_activation.result.checked_at')}</span>
          <span className="font-mono text-dense text-paper-mid">{response.checked_at}</span>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-label text-paper-low">{t('sku_activation.result.first_blocker')}</span>
          {blocker === null ? (
            <span className="text-dense text-ok">{t('sku_activation.result.none')}</span>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-dense text-bad">{blocker.code}</span>
              {blocker.deep_link === undefined ? null : (
                <Button size="sm" variant="ghost" onClick={() => onNavigate(blocker.deep_link!)}>
                  {t('sku_activation.action.open')}
                  <ArrowRight aria-hidden="true" className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </Panel>
  );
}

function EvidencePanel({
  check,
  onNavigate,
}: {
  readonly check: SkuActivationCheck | null;
  readonly onNavigate: (path: string) => void;
}): JSX.Element {
  const t = useT();
  return (
    <Panel caption={t('sku_activation.evidence.title')} padded testId="sku-activation-evidence-panel">
      {check === null ? (
        <p className="m-0 text-dense text-paper-low">{t('sku_activation.evidence.empty')}</p>
      ) : (
        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <Chip tone={statusTone(check.status)}>{statusLabel(check.status, t)}</Chip>
              <code className="text-micro text-paper-low">{check.id}</code>
            </div>
            <h3 className="m-0 font-mono text-body text-paper">{check.code}</h3>
            <p className="m-0 text-dense text-paper-mid">{check.message}</p>
          </div>
          <div className="rounded border border-line-soft bg-well p-3">
            <p className="mb-2 mt-0 text-label text-paper-low">{t('sku_activation.evidence.action')}</p>
            <code className="break-all text-micro text-paper-mid">{check.child_endpoint}</code>
            <p className="mb-0 mt-2 text-label text-warn">{t('sku_activation.evidence.read_only_note')}</p>
          </div>
          <dl className="m-0 flex flex-col gap-2">
            {Object.entries(check.evidence).length === 0 ? (
              <p className="m-0 text-dense text-paper-low">{t('sku_activation.evidence.none')}</p>
            ) : Object.entries(check.evidence).map(([key, value]) => (
              <div key={key} className="grid grid-cols-2 gap-3 border-b border-line-soft pb-2">
                <dt className="font-mono text-micro text-paper-low">{key}</dt>
                <dd className="m-0 break-all text-right font-mono text-micro text-paper-mid">
                  {Array.isArray(value) ? value.join(', ') : String(value)}
                </dd>
              </div>
            ))}
          </dl>
          <div className="flex flex-col gap-2 border-t border-line-soft pt-4">
            <span className="text-label text-paper-low">{t('sku_activation.evidence.rollback')}</span>
            <span className="text-dense text-paper-mid">
              {check.evidence.rollback_operation_id === undefined
                ? t('sku_activation.evidence.rollback_none')
                : String(check.evidence.rollback_operation_id)}
            </span>
          </div>
          {check.deep_link === undefined ? null : (
            <Button variant="secondary" onClick={() => onNavigate(check.deep_link!)}>
              {t('sku_activation.action.open_source')}
            </Button>
          )}
        </div>
      )}
    </Panel>
  );
}

function LineagePanel({ response }: { readonly response: SkuActivationReadinessResponse }): JSX.Element {
  const t = useT();
  return (
    <Panel caption={t('sku_activation.lineage.title')} count={response.route_lineage.length} testId="sku-activation-lineage">
      {response.route_lineage.length === 0 ? (
        <p className="m-0 px-4 py-3 text-dense text-paper-low">{t('sku_activation.lineage.empty')}</p>
      ) : (
        <div className="divide-y divide-line-soft">
          {response.route_lineage.map((row) => (
            <div key={`${row.sku_id}:${row.path}`} className="grid grid-cols-1 gap-2 px-4 py-3 lg:grid-cols-3">
              <div className="flex flex-col gap-0.5">
                <span className="font-mono text-dense text-paper">{row.requested_model}</span>
                <span className="font-mono text-micro text-paper-low">{row.sku_id} · {row.path}</span>
              </div>
              <div className="flex items-center gap-2 font-mono text-micro text-paper-mid">
                <span>{row.expected_partition}</span>
                <ArrowRight aria-hidden="true" className="h-3.5 w-3.5 text-paper-faint" />
                <Chip tone={row.effective_partition === 'sku_partition' ? 'ok' : 'bad'} lamp={false}>
                  {row.effective_partition}
                </Chip>
              </div>
              <div className="flex flex-col gap-0.5 text-right font-mono text-micro text-paper-low">
                <span>{row.active_config_id ?? t('sku_activation.lineage.default')}</span>
                <span>{row.target_id ?? t('sku_activation.lineage.no_target')}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}
